import { useEffect, useRef } from "react";

interface KaiVoiceLevelBarsProps {
  /** 0..1 combined mic/agent level, polled per frame. */
  getLevel: () => number;
  /** Harness mode: one static frame from a fixed pattern (VR determinism). */
  frozen?: boolean;
}

const BAR_COUNT = 14;
const FROZEN_PATTERN = [0.2, 0.45, 0.7, 0.5, 0.85, 0.6, 0.35, 0.55, 0.75, 0.4, 0.65, 0.3, 0.5, 0.25];

/** The little live-level pill under the captions (Fin-style dot row). */
export const KaiVoiceLevelBars = ({ getLevel, frozen }: KaiVoiceLevelBarsProps) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const getLevelRef = useRef(getLevel);
  getLevelRef.current = getLevel;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) {
      return;
    }
    const context = canvas.getContext("2d");
    if (!context) {
      return;
    }

    const history: number[] = frozen ? [...FROZEN_PATTERN] : new Array(BAR_COUNT).fill(0.08);
    let rafId: number | null = null;
    const reducedMotion =
      frozen || (window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches ?? false);

    const draw = () => {
      const dpr = window.devicePixelRatio || 1;
      const width = canvas.clientWidth;
      const height = canvas.clientHeight;
      if (canvas.width !== width * dpr || canvas.height !== height * dpr) {
        canvas.width = width * dpr;
        canvas.height = height * dpr;
        context.setTransform(dpr, 0, 0, dpr, 0, 0);
      }

      if (!frozen) {
        history.push(Math.min(Math.max(getLevelRef.current() ?? 0, 0.08), 1));
        history.shift();
      }

      context.clearRect(0, 0, width, height);
      const gap = width / BAR_COUNT;
      const barWidth = Math.max(gap * 0.35, 2);

      history.forEach((level, index) => {
        const barHeight = Math.max(height * level * 0.85, barWidth);
        const x = index * gap + (gap - barWidth) / 2;
        const y = (height - barHeight) / 2;
        context.fillStyle = `rgba(190, 235, 220, ${0.35 + level * 0.6})`;
        context.beginPath();
        const radius = barWidth / 2;
        // roundRect is missing from older Safari + the TS 4.6 dom lib.
        const roundRect = (context as any).roundRect?.bind(context);
        if (roundRect) {
          roundRect(x, y, barWidth, barHeight, radius);
        } else {
          context.rect(x, y, barWidth, barHeight);
        }
        context.fill();
      });

      if (!reducedMotion) {
        rafId = requestAnimationFrame(draw);
      }
    };

    rafId = requestAnimationFrame(draw);
    return () => {
      if (rafId != null) {
        cancelAnimationFrame(rafId);
      }
    };
  }, [frozen]);

  return (
    <div className="kai-voice-call--level">
      <canvas className="kai-voice-call--level-canvas" ref={canvasRef} />
    </div>
  );
};
